import * as React from "react";
import {
    CircularProgress,
    createStyles,
    Fab,
    Theme,
    Typography,
    withStyles,
    WithStyles,
    Zoom
} from "@material-ui/core";
import { IParcelBid } from "../../../models/parcel/IParcelBid";
import { BidLoadState, LoadState } from "../../../models/utility/LoadState";
import { Numbers } from "../../../models/utility/Numbers";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import * as ParcelBidStore from "../../../store/ParcelBidStore";
import * as GlobalTheme from "../../../styles/index";
import * as ParcelBidNodeCss from "../../../styles/ParcelBidNodeCss";
import ParcelBidHistoryNode from "./ParcelBidHistoryNode";
import ParcelBidTimeline from "./ParcelBidTimeline";

type ParcelBidHistoryProps = WithStyles<
    typeof styles | typeof ParcelBidNodeCss.styles
> & {
    bidAmount: number;
    bidIncrement: number;
    canPlaceBid: boolean;
    parcelId: string;
};

const styles = (theme: Theme) =>
    createStyles({
        circleStart: {
            [theme.breakpoints.down("xs")]: {
                height: 12,
                width: 12
            },
            borderRadius: "50%",
            height: 15,
            position: "relative",
            width: 15
        },
        loaderWrapper: {
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            margin: theme.spacing(3),
            width: "100%"
        },
        ...ParcelBidNodeCss.styles(theme)
    });

const noBids: IParcelBid[] = [];

// Sizes the strip to the viewport, leaving room for the surrounding layout.
const stripWidth = (viewWidth: number) => {
    const maxBodyWidth =
        GlobalTheme.maxBodyWidth + GlobalTheme.desktopPadding * 2;

    if (viewWidth < maxBodyWidth && viewWidth >= 960) {
        return viewWidth - Math.round(viewWidth * 0.28);
    } else if (viewWidth < 960 && viewWidth >= 600) {
        return viewWidth - 80;
    } else if (viewWidth < 600) {
        return viewWidth - 50;
    }
    return viewWidth - 310;
};

const timelinePosition = (
    isFirst: boolean,
    endsStrip: boolean
): ParcelBidNodeCss.TimelinePosition => {
    if (isFirst) return endsStrip ? "single" : "start";
    return endsStrip ? "end" : "middle";
};

function ParcelBidHistory({
    bidAmount,
    bidIncrement,
    canPlaceBid,
    classes,
    parcelId
}: ParcelBidHistoryProps) {
    const dispatch = useAppDispatch();
    const parcelBids = useAppSelector(
        (state) => state.parcelBidStoreState.parcelBidDict[parcelId] ?? noBids
    );
    const parcelBidLoadState = useAppSelector(
        (state) => state.parcelBidStoreState.parcelBidLoadState
    );
    const user = useAppSelector((state) => state.userStoreState.user);
    const myBidIds = useAppSelector(
        (state) => state.parcelBidStoreState.myBidIds
    );
    const rootRef = React.useRef<HTMLDivElement>(null);

    // Scrolls as far right as possible, so the newest bid is in view.
    const scrollRootDivRight = React.useCallback(() => {
        const timeout = setTimeout(() => {
            if (rootRef.current) rootRef.current.scrollLeft += 10000;
        }, 500);
        return () => clearTimeout(timeout);
    }, []);

    React.useEffect(() => {
        let cancelScroll = () => {};
        const resizeRootDiv = () => {
            rootRef.current?.setAttribute(
                "style",
                `width: ${stripWidth(window.innerWidth)}px;`
            );
            cancelScroll();
            cancelScroll = scrollRootDivRight();
        };

        window.addEventListener("deviceorientation", resizeRootDiv);
        window.addEventListener("orientationchange", resizeRootDiv);
        window.addEventListener("resize", resizeRootDiv);
        resizeRootDiv();

        return () => {
            window.removeEventListener("deviceorientation", resizeRootDiv);
            window.removeEventListener("orientationchange", resizeRootDiv);
            window.removeEventListener("resize", resizeRootDiv);
            cancelScroll();
        };
    }, [scrollRootDivRight]);

    React.useEffect(scrollRootDivRight, [
        bidAmount,
        bidIncrement,
        canPlaceBid,
        parcelBids,
        parcelBidLoadState,
        scrollRootDivRight,
        user
    ]);

    const openBidDialog = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        dispatch(ParcelBidStore.actionCreators.toggleDialogVisible(true));
        dispatch(
            ParcelBidStore.actionCreators.updateCurrentBid(
                parcelId,
                BidLoadState.NotSubmitted
            )
        );
    };

    if (parcelBidLoadState !== LoadState.Loaded) {
        return (
            <div className={classes.root} ref={rootRef}>
                <div className={classes.loaderWrapper}>
                    <CircularProgress color="secondary" />
                </div>
            </div>
        );
    }

    const bidCount = parcelBids.length;
    const newBidAmt = bidAmount + (bidCount > 0 ? bidIncrement : 0);
    const showBidButton =
        !!user &&
        canPlaceBid &&
        !ParcelBidStore.holdsHighBid(myBidIds, parcelBids);

    return (
        <div className={classes.root} ref={rootRef}>
            {bidCount === 0 && !showBidButton && (
                <div className={classes.bidNodeWrapper}>
                    <div className={classes.bidBlockTop}>
                        <Typography
                            className={`${classes.textBase} ${classes.textTop} ${classes.auctionStartText}`}
                            component="div"
                            variant="button"
                        >
                            Starting Bid
                        </Typography>
                    </div>
                    <div className={classes.bidBlockMiddle}>
                        <div className={classes.circleStart} />
                    </div>
                    <div className={classes.bidBlockBottom} />
                </div>
            )}

            {parcelBids.map((bid, idx) => {
                const isCurrentUser = ParcelBidStore.isMyBid(myBidIds, bid);
                const isFirst = idx === 0;
                const endsStrip =
                    idx === bidCount - 1 && (isCurrentUser || !canPlaceBid);
                const bidPosition = timelinePosition(isFirst, endsStrip);

                return (
                    <ParcelBidHistoryNode
                        key={bid.parcelBidId}
                        bid={bid}
                        bidPosition={bidPosition}
                        isFirstBid={isFirst}
                        isCurrentUser={isCurrentUser}
                    />
                );
            })}

            <Zoom in={showBidButton}>
                <div className={classes.bidNodeWrapper}>
                    {bidCount > 0 && (
                        <ParcelBidTimeline classes={classes} position="end" />
                    )}

                    <div className={classes.bidBlockTop}>
                        {bidCount === 0 && showBidButton && (
                            <Typography
                                className={`${classes.textBase} ${classes.textTop} ${classes.auctionStartText}`}
                                component="div"
                                variant="button"
                            >
                                Starting Bid
                            </Typography>
                        )}
                    </div>
                    <div className={classes.bidBlockMiddle}>
                        <Fab
                            className={classes.bidButton}
                            variant="circular"
                            color="primary"
                            size="small"
                            onClick={openBidDialog}
                        >
                            Bid
                        </Fab>
                    </div>
                    <div className={classes.bidBlockBottom}>
                        <Typography
                            className={`${classes.textTime} ${classes.textBase} ${classes.textBidNext}`}
                            component="div"
                            variant="caption"
                            color="primary"
                        >
                            {Numbers.toShortFormat(newBidAmt)}
                        </Typography>
                    </div>
                </div>
            </Zoom>
        </div>
    );
}

export default withStyles(styles)(React.memo(ParcelBidHistory));
